import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap select-none",
    "font-medium tracking-tight rounded-md",
    "transition-[background-color,color,border-color,box-shadow,transform]",
    "duration-[var(--dur-fast)] ease-[var(--ease-out)]",
    "outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-0",
    "disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    "active:scale-[0.985]",
  ),
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-fg hover:bg-primary-hover active:bg-primary-active shadow-xs",
        destructive:
          "bg-danger text-danger-fg hover:bg-danger/90 shadow-xs",
        warning:
          "bg-warning text-warning-fg hover:bg-warning/90 shadow-xs",
        outline:
          "border border-border bg-surface text-fg hover:bg-surface-hover hover:border-border-strong",
        secondary:
          "bg-bg-subtle text-fg hover:bg-surface-hover border border-border-subtle",
        ghost:
          "text-fg-muted hover:text-fg hover:bg-surface-hover",
        link:
          "text-primary underline-offset-4 hover:underline px-0 h-auto",
        outline_primary:
          "border border-primary/40 text-primary bg-primary-soft/50 hover:bg-primary-soft hover:border-primary/60",
        ai:
          "ai-gradient-strong text-white shadow-sm hover:opacity-92 [&_svg]:text-white",
        nothing:
          "text-primary p-0 h-auto",
      },
      size: {
        default: "h-9 px-3.5 text-sm [&_svg]:size-4",
        sm:      "h-8 px-3 text-xs [&_svg]:size-3.5 rounded-md",
        lg:      "h-10 px-5 text-sm [&_svg]:size-4 rounded-md",
        xl:      "h-11 px-6 text-[15px] [&_svg]:size-[18px] rounded-lg",
        icon:    "h-9 w-9 [&_svg]:size-4",
        iconSm:  "h-8 w-8 [&_svg]:size-3.5",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Spinner = () => (
  <svg className="animate-spin" viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2.5" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    // When asChild=true, Comp is Radix's Slot — which uses React.Children.only
    // and rejects anything other than a single React element. We can't inject
    // a spinner sibling in that case (and the asChild target is usually an
    // anchor/Link where `loading` doesn't have a natural meaning anyway), so
    // just pass children through. For the regular <button> branch, render the
    // spinner alongside as before.
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        data-loading={loading || undefined}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading ? <Spinner /> : null}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
